import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Skeleton } from "../components/ui/skeleton";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { 
  AlertTriangle, 
  HeartPulse, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Smartphone,
  Eye,
  Crown,
  RefreshCw,
  Zap,
  ThumbsUp,
  ThumbsDown,
  Building2,
  Star,
  CalendarIcon,
  Repeat,
  ShieldAlert,
  Wrench,
  AlertCircle,
  Pause,
  Droplet,
  Sparkles,
  Dumbbell,
  Baby,
  Volume2,
  ParkingCircle,
  Search,
  Snowflake,
  Droplets,
  Flame,
  DoorClosed,
  Lightbulb,
  Armchair,
  ShowerHead,
  Blinds,
  ArrowUpDown,
  Home,
  Wind,
  Tv,
  Wifi,
  Phone,
  Shirt,
  Package,
  Bug,
  CheckCircle,
  FileWarning,
  Receipt,
  Bell,
  UtensilsCrossed,
  Coffee
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIOD_OPTIONS = [
  { value: "this_month", label: "This Month" },
  { value: "prev_month", label: "Previous Month" },
  { value: "ytd", label: "Year to Date (YTD)" },
  { value: "custom", label: "Custom Range" },
];

const getDateRange = (period) => {
  const now = new Date();
  switch (period) {
    case "this_month":
      return { start: startOfMonth(now), end: now };
    case "prev_month":
      return { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) };
    case "ytd":
      return { start: new Date(now.getFullYear(), 0, 1), end: now };
    default:
      return { start: startOfMonth(now), end: now };
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [recentIssues, setRecentIssues] = useState([]);
  const [departmentScorecard, setDepartmentScorecard] = useState([]);
  const [sentimentAudit, setSentimentAudit] = useState([]);
  const [criticalCluster, setCriticalCluster] = useState({ total_critical: 0, issues: [] });
  const [topIssues, setTopIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [deptIssues, setDeptIssues] = useState([]);
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryIssues, setCategoryIssues] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [period, setPeriod] = useState("this_month");
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [selectedTopIssue, setSelectedTopIssue] = useState(null);
  const [topIssueDialogOpen, setTopIssueDialogOpen] = useState(false);
  const [predictionDialogOpen, setPredictionDialogOpen] = useState(false);
  const [slaBreaches, setSlaBreaches] = useState([]);
  const [slaDialogOpen, setSlaDialogOpen] = useState(false);
  const [recurringProblems, setRecurringProblems] = useState({ recurring_problems: [], summary: {} });
  const [recurringSortBy, setRecurringSortBy] = useState("frequency");
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  const [topRooms, setTopRooms] = useState([]);
  const [selectedTopRoom, setSelectedTopRoom] = useState(null);
  const [topRoomDialogOpen, setTopRoomDialogOpen] = useState(false);

  const handleSlaClick = async () => {
    try {
      const { start, end } = getActiveDateRange();
      const params = new URLSearchParams();
      if (start) params.append("start_date", start.toISOString());
      if (end) params.append("end_date", end.toISOString());
      const res = await axios.get(`${API}/dashboard/sla-breaches?${params.toString()}`);
      setSlaBreaches(res.data);
      setSlaDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load SLA breaches");
    }
  };

  const handlePendingClick = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/pending-items`);
      setPendingItems(res.data);
      setPendingDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load pending items");
    }
  };

  const getActiveDateRange = () => {
    if (period === "custom" && customRange.from) {
      return { start: customRange.from, end: customRange.to || new Date() };
    }
    return getDateRange(period);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { start, end } = getActiveDateRange();
      const params = new URLSearchParams();
      if (start) params.append("start_date", start.toISOString());
      if (end) params.append("end_date", end.toISOString());
      const queryStr = params.toString() ? `?${params.toString()}` : "";
      
      const [kpiRes, predRes, issuesRes, scorecardRes, auditRes, criticalRes, topRes, recurringRes, topRoomsRes] = await Promise.all([
        axios.get(`${API}/dashboard/kpi${queryStr}`),
        axios.get(`${API}/dashboard/predictions${queryStr}`),
        axios.get(`${API}/issues?limit=5${queryStr ? '&' + params.toString() : ''}`),
        axios.get(`${API}/dashboard/department-scorecard${queryStr}`),
        axios.get(`${API}/dashboard/sentiment-audit${queryStr}`),
        axios.get(`${API}/dashboard/critical-cluster${queryStr}`),
        axios.get(`${API}/dashboard/top-issues${queryStr}`),
        axios.get(`${API}/dashboard/recurring-problems${queryStr}${queryStr ? "&" : "?"}sort_by=${recurringSortBy}`),
        axios.get(`${API}/dashboard/top-rooms${queryStr}${queryStr ? "&" : "?"}limit=5`),
      ]);
      setKpi(kpiRes.data);
      setPredictions(predRes.data);
      setRecentIssues(issuesRes.data);
      setDepartmentScorecard(scorecardRes.data);
      setSentimentAudit(auditRes.data);
      setCriticalCluster(criticalRes.data);
      setTopIssues(topRes.data);
      setRecurringProblems(recurringRes.data);
      setTopRooms(topRoomsRes.data);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    const savedScroll = sessionStorage.getItem("dashboard_scroll_pos");
    if (savedScroll) {
      const container = document.getElementById("main-scroll-container");
      if (container) container.scrollTop = parseInt(savedScroll, 10);
      sessionStorage.removeItem("dashboard_scroll_pos");
    }
  }, [loading]);

  useEffect(() => {
    fetchData();
    // Removed auto refresh - causes data loss
    // const interval = setInterval(fetchData, 30000);
    // return () => clearInterval(interval);
  }, [period, customRange, recurringSortBy]);

  const handleDeptClick = async (dept, type = null) => {
    setSelectedDept(dept);
    setSelectedType(type);
    try {
      const { start, end } = getActiveDateRange();
      const params = new URLSearchParams();
      if (type) params.append("issue_type", type);
      if (start) params.append("start_date", start.toISOString());
      if (end) params.append("end_date", end.toISOString());
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API}/dashboard/department-issues/${dept}${queryStr}`);
      setDeptIssues(res.data);
      setDeptDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load department issues");
    }
  };

  const handleCategoryClick = async (category) => {
    setSelectedCategory(category);
    try {
      const { start, end } = getActiveDateRange();
      const params = new URLSearchParams();
      if (start) params.append("start_date", start.toISOString());
      if (end) params.append("end_date", end.toISOString());
      const queryStr = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API}/dashboard/category-issues/${encodeURIComponent(category)}${queryStr}`);
      setCategoryIssues(res.data);
      setCategoryDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load category issues");
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      S4: "bg-red-500/10 text-red-500 border-red-500/20",
      S3: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      S2: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      S1: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      P1: "bg-purple-500/10 text-purple-500 border-purple-500/20",
    };
    return colors[severity] || colors.S2;
  };

  const getStatusColor = (status) => {
    const colors = {
      OPEN: "bg-red-500/10 text-red-500 border-red-500/20",
      ACKNOWLEDGED: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      IN_PROGRESS: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      RESOLVED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      CLOSED: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
    };
    return colors[status] || colors.OPEN;
  };

  const getDefectTypeBadge = (defectType) => {
    const map = {
      GUEST_DEFECT: { label: "Guest", className: "bg-red-500/10 text-red-400 border-red-500/20" },
      DMS_LOG: { label: "DMS", className: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
      MOD: { label: "MOD", className: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
      RPM_GC: { label: "RPM", className: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
    };
    return map[defectType] || null;
  };

  const getDeptFullName = (code) => {
    const names = {
      ENG: "Engineering",
      HK: "Housekeeping",
      FO: "Front Office",
      FNB: "F&B",
      SEC: "Security",
      IT: "IT",
      MGT: "General"
    };
    return names[code] || code;
  };

  if (loading && !kpi) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-foreground">
              Executive Cockpit
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Real-time operational intelligence
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Period Selector */}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[180px]" data-testid="period-selector">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Custom Date Range */}
            {period === "custom" && (
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="custom-date-btn">
                    {customRange.from ? (
                      customRange.to ? (
                        `${format(customRange.from, "dd/MM/yy")} - ${format(customRange.to, "dd/MM/yy")}`
                      ) : format(customRange.from, "dd/MM/yy")
                    ) : "Pick dates"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={customRange}
                    onSelect={(range) => {
                      setCustomRange(range || { from: null, to: null });
                      if (range?.to) setCalendarOpen(false);
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            )}
            
            <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* NEW KPI Cards - Clean Modern Design */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="orbit-card">
                <CardContent className="p-5">
                  <Skeleton className="h-4 w-24 mb-3" />
                  <Skeleton className="h-12 w-20 mb-3" />
                  <Skeleton className="h-2 w-full mb-2" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Operational Health */}
          <Card className="orbit-card bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/30" data-testid="kpi-health">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider text-emerald-400 font-medium flex items-center gap-2">
                  <HeartPulse className="w-4 h-4" />
                  Operational Health
                </p>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <p className="font-heading text-5xl font-bold text-emerald-400">
                  {kpi?.property_health_index ?? 100}%
                </p>
                {kpi?.property_health_delta != null && kpi.property_health_delta !== 0 && (
                  <span className={`text-xs flex items-center ${kpi.property_health_delta > 0 ? "text-emerald-500" : "text-red-400"}`}>
                    <TrendingUp size={12} className={`mr-1 ${kpi.property_health_delta < 0 ? "rotate-180" : ""}`} />
                    {kpi.property_health_delta > 0 ? "+" : ""}{kpi.property_health_delta}%
                  </span>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Target: 80%</span>
                  <span>80%</span>
                </div>
                <Progress value={kpi?.property_health_index ?? 100} className="h-2 bg-emerald-950" />
              </div>
              {kpi?.property_health_delta != null && (
                <p className={`text-xs mt-3 flex items-center gap-1 ${kpi.property_health_delta > 0 ? "text-emerald-500/70" : kpi.property_health_delta < 0 ? "text-red-400/70" : "text-muted-foreground"}`}>
                  <span className={`w-2 h-2 rounded-full ${kpi.property_health_delta > 0 ? "bg-emerald-500" : kpi.property_health_delta < 0 ? "bg-red-400" : "bg-muted-foreground"}`}></span>
                  {kpi.property_health_delta > 0 ? "Membaik" : kpi.property_health_delta < 0 ? "Menurun" : "Stabil"} dibandingkan periode lalu
                </p>
              )}
            </CardContent>
          </Card>

          {/* Guest Sentiment */}
          <Card className="orbit-card bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30" data-testid="kpi-sentiment">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider text-amber-400 font-medium flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Guest Sentiment
                </p>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <p className="font-heading text-5xl font-bold text-amber-400">
                  {kpi?.sentiment_index?.toFixed(1) || "0.0"}
                </p>
                {kpi?.sentiment_delta != null && kpi.sentiment_delta !== 0 && (
                  <span className={`text-xs flex items-center ${kpi.sentiment_delta > 0 ? "text-emerald-500" : "text-red-400"}`}>
                    <TrendingUp size={12} className={`mr-1 ${kpi.sentiment_delta < 0 ? "rotate-180" : ""}`} />
                    {kpi.sentiment_delta > 0 ? "+" : ""}{kpi.sentiment_delta}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Target: 4.5</span>
                  <span>4.5</span>
                </div>
                <Progress value={(kpi?.sentiment_index / 5) * 100 || 0} className="h-2 bg-amber-950" />
              </div>
              {kpi?.sentiment_delta != null && (
                <p className={`text-xs mt-3 flex items-center gap-1 ${kpi.sentiment_delta > 0 ? "text-emerald-500/70" : kpi.sentiment_delta < 0 ? "text-red-400/70" : "text-muted-foreground"}`}>
                  <span className={`w-2 h-2 rounded-full ${kpi.sentiment_delta > 0 ? "bg-emerald-500" : kpi.sentiment_delta < 0 ? "bg-red-400" : "bg-muted-foreground"}`}></span>
                  {kpi.sentiment_delta > 0 ? "Membaik" : kpi.sentiment_delta < 0 ? "Sedikit menurun" : "Stabil"} dibandingkan periode lalu
                </p>
              )}
            </CardContent>
          </Card>

          {/* Pending Items */}
          <Card 
            className="orbit-card bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/30 cursor-pointer hover:border-purple-500/50 transition-colors" 
            data-testid="kpi-pending"
            onClick={handlePendingClick}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider text-purple-400 font-medium flex items-center gap-2">
                  <Pause className="w-4 h-4" />
                  Pending Items
                </p>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <p className="font-heading text-5xl font-bold text-purple-400">
                  {kpi?.pending_count || 0}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">Menunggu tindakan</p>
              <p className="text-xs text-purple-500/70 mt-3 flex items-center gap-1">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Klik untuk melihat detail
              </p>
            </CardContent>
          </Card>

          {/* Overdue Issues */}
          <Card 
            className="orbit-card bg-gradient-to-br from-red-500/10 to-transparent border-red-500/30 cursor-pointer hover:border-red-500/50 transition-colors" 
            data-testid="kpi-overdue"
            onClick={handleSlaClick}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider text-red-400 font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Overdue Issues
                </p>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <p className="font-heading text-5xl font-bold text-red-400">
                  {kpi?.sla_breach_count || 0}
                </p>
              </div>
              <p className="text-sm text-red-400">Perlu perhatian</p>
              {kpi?.sla_breach_delta != null && (
                <p className="text-xs text-red-500/70 mt-3 flex items-center gap-1">
                  {kpi.sla_breach_delta !== 0 ? (
                    <>
                      <TrendingUp size={12} className={kpi.sla_breach_delta < 0 ? "rotate-180" : ""} />
                      {kpi.sla_breach_delta > 0 ? "+" : ""}{kpi.sla_breach_delta} dibandingkan periode lalu
                    </>
                  ) : (
                    "Stabil dibandingkan periode lalu"
                  )}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
        )}

        {/* TOP PROBLEMS TODAY - disembunyikan sementara */}
        {false && (
        <Card className="orbit-card" data-testid="top-problems">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Top Problems Today
                <span className="text-xs text-muted-foreground font-normal normal-case ml-2">
                  Masalah paling kritikal yang perlu ditangani sekarang
                </span>
              </CardTitle>
              <Link to="/issues" className="text-xs text-primary hover:underline flex items-center gap-1">
                Lihat semua <ArrowRight size={12} />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-36 w-full rounded-lg" />
                ))}
              </div>
            ) : criticalCluster.issues.length > 0 ? (
              <div className="border border-border rounded-sm overflow-hidden">
                {criticalCluster.issues.slice(0, 4).map((issue, idx) => {
                  const isVip = issue.guest_type === "VIP";
                  const isS4 = issue.final_severity === "S4";
                  const isCritical = issue.risk_level === "CRITICAL";
                  const isSlaBreached = issue.sla_breached;

                  let reasonLabel = "LOW";
                  let reasonColor = "text-blue-400";
                  let dotColor = "bg-blue-500";

                  if (isS4 || isCritical) {
                    reasonLabel = "CRITICAL";
                    reasonColor = "text-red-400";
                    dotColor = "bg-red-500";
                  } else if (isSlaBreached) {
                    reasonLabel = "OVERDUE";
                    reasonColor = "text-amber-400";
                    dotColor = "bg-amber-500";
                  } else if (isVip) {
                    reasonLabel = "VIP";
                    reasonColor = "text-purple-400";
                    dotColor = "bg-purple-500";
                  }

                  const getAge = (createdAt) => {
                    if (!createdAt) return "-";
                    const diffMs = new Date() - new Date(createdAt);
                    const diffMin = Math.floor(diffMs / 60000);
                    if (diffMin < 60) return `${diffMin} Menit`;
                    const diffHour = Math.floor(diffMin / 60);
                    return `${diffHour} Jam`;
                  };

                  return (
                    <div
                      key={issue.id}
                      className={`grid grid-cols-[16px_100px_1fr_120px_90px_80px] items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors ${idx > 0 ? "border-t border-border" : ""}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />
                      <span className="text-sm font-medium truncate flex items-center gap-1" title={`Room ${issue.room_number}`}>
                        {isVip && <Crown className="w-3 h-3 text-amber-400 shrink-0" />}
                        Room {issue.room_number}
                      </span>
                      <span className="text-sm text-muted-foreground truncate">
                        {issue.description || "Issue"}
                      </span>
                      <Badge variant="outline" className="text-xs w-fit">
                        {getDeptFullName(issue.department_owner)}
                      </Badge>
                      <span className={`text-xs font-medium ${reasonColor}`}>
                        {reasonLabel}
                      </span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getAge(issue.created_at)}
                      </span>
                    </div>
                  );
                })}

              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>Tidak ada masalah kritikal saat ini</p>
              </div>
            )}
          </CardContent>
        </Card>
)}

        {/* 5.2 Department Scorecard */}
        <Card className="orbit-card" data-testid="department-scorecard">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              Department Scorecard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {departmentScorecard.map((dept) => (
                <button
                  key={dept.department}
                  onClick={() => handleDeptClick(dept.department)}
                  className="p-4 bg-surface-highlight/50 border border-border rounded-sm hover:border-primary/50 transition-all text-left group"
                  data-testid={`dept-${dept.department}`}
                >
                  <p className="font-heading text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                    {dept.department === 'MGT' ? 'GEN' : dept.department}
                  </p>
                  <p className="text-[10px] text-muted-foreground mb-3">{getDeptFullName(dept.department)}</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeptClick(dept.department, 'praise'); }}
                        className="flex items-center gap-1 hover:bg-emerald-500/20 px-1 rounded transition-colors"
                        title="View praise comments"
                      >
                        <ThumbsUp size={10} className="text-emerald-500" />
                        <span className="text-xs text-emerald-500">{dept.praise_points}</span>
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeptClick(dept.department, 'defect'); }}
                        className="flex items-center gap-1 hover:bg-red-500/20 px-1 rounded transition-colors"
                        title="View defect comments"
                      >
                        <ThumbsDown size={10} className="text-red-500" />
                        <span className="text-xs text-red-500">{dept.defect_points}</span>
                      </button>
                    </div>
                    <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                        style={{ width: `${dept.score}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-center text-muted-foreground">{dept.score}% positive</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* 5.3 Sentiment Audit (TrustYou Style) */}
        <Card className="orbit-card" data-testid="sentiment-audit">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
              <Star className="w-4 h-4 text-primary" />
              Sentiment Audit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs uppercase tracking-wider text-muted-foreground py-3 px-2">Category</th>
                    <th className="text-center text-xs uppercase tracking-wider text-muted-foreground py-3 px-2">Score</th>
                    <th className="text-center text-xs uppercase tracking-wider text-muted-foreground py-3 px-2">Mentions</th>
                    <th className="text-left text-xs uppercase tracking-wider text-muted-foreground py-3 px-2 w-1/3">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {sentimentAudit.map((item) => (
                    <tr 
                      key={item.category} 
                      className="border-b border-border/50 hover:bg-surface-highlight/30 cursor-pointer"
                      onClick={() => handleCategoryClick(item.category)}
                    >
                      <td className="py-3 px-2">
                        <span className="text-sm font-medium hover:text-primary">{item.category}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`font-heading text-xl font-bold ${
                          item.avg_score >= 4 ? 'text-emerald-500' :
                          item.avg_score >= 3 ? 'text-primary' :
                          item.avg_score >= 2 ? 'text-amber-500' : 'text-red-500'
                        }`}>
                          {item.avg_score}
                        </span>
                        <span className="text-xs text-muted-foreground">/5</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="text-sm text-muted-foreground">{item.total_mentions}</span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-4 bg-surface rounded-full overflow-hidden flex">
                            <div 
                              className="h-full bg-emerald-500"
                              style={{ width: `${item.positive_pct}%` }}
                              title={`Positive: ${item.positive_pct}%`}
                            />
                            <div 
                              className="h-full bg-zinc-600"
                              style={{ width: `${100 - item.positive_pct - item.negative_pct}%` }}
                              title="Neutral"
                            />
                            <div 
                              className="h-full bg-red-500"
                              style={{ width: `${item.negative_pct}%` }}
                              title={`Negative: ${item.negative_pct}%`}
                            />
                          </div>
                          <div className="flex gap-2 text-[10px] min-w-[80px]">
                            <span className="text-emerald-500">{item.positive}</span>
                            <span className="text-zinc-500">{item.neutral}</span>
                            <span className="text-red-500">{item.negative}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Top 3 Recurrent Issues - disembunyikan, digantikan Recurring Problem Detector */}
        {false && (

        <Card className="orbit-card" data-testid="top-issues">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-orange-500" />
              Monthly Summary — Top 3 Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topIssues.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No recurring issues in this period</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {topIssues.map((item, idx) => (
                  <div
                    key={item.category}
                    className="p-5 bg-muted/30 rounded-sm cursor-pointer hover:bg-muted/50 transition-colors border border-border"
                    onClick={() => {
                      setSelectedTopIssue(item);
                      setTopIssueDialogOpen(true);
                    }}
                  >
                    <span className="text-xs font-bold text-orange-500">#{idx + 1}</span>
                    <p className="text-sm font-medium mt-2 mb-1">{item.category_name}</p>
                    <p className="text-2xl font-bold">
                      {item.count}<span className="text-sm text-muted-foreground font-normal">x</span>
                    </p>
                    {item.trend_pct != null && (
                      <div className={`text-xs flex items-center gap-1 mt-1 ${item.trend_direction === 'up' ? 'text-red-500' : item.trend_direction === 'down' ? 'text-green-500' : 'text-muted-foreground'}`}>
                        {item.trend_direction === 'up' ? '↑' : item.trend_direction === 'down' ? '↓' : '→'}
                        {Math.abs(item.trend_pct)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        <div className="grid grid-cols-1 gap-6">
          {/* 5.5 Recurring Problem Detector (AI-Powered) */}
          <Card className="orbit-card" data-testid="recurring-problems-detector">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
                <Repeat className="w-4 h-4 text-orange-500" />
                Recurring Problem Detector
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                AI-powered pattern detection • {
                  period === "this_month" ? "This Month" :
                  period === "prev_month" ? "Previous Month" :
                  period === "ytd" ? "Year to Date" :
                  period === "custom" ? "Custom Range" : "Last 30 Days"
                }
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* Summary Stats */}
              {recurringProblems.summary && (
                <div className="flex items-center gap-3 mb-3 p-2 bg-surface-highlight/30 rounded">
                  <div className="text-center flex-1">
                    <p className="text-lg font-bold text-orange-400">{recurringProblems.summary.total_recurring || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Recurring</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-lg font-bold text-red-400">{recurringProblems.summary.trending_up_count || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Trending Up</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-lg font-bold text-amber-400">{recurringProblems.summary.critical_count || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Critical</p>
                  </div>
                </div>
              )}
              
              {recurringProblems.recurring_problems?.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Repeat className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No recurring patterns detected</p>
                  <p className="text-xs mt-1">Great job maintaining quality!</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[900px] pr-2">
                  <div className="space-y-2">
                    {recurringProblems.recurring_problems?.slice(0, 8).map((problem, idx) => {
                      const impactColors = {
                        "Guest Safety Risk": { border: "border-red-300", bg: "bg-red-500/5", icon: "bg-red-500/15 text-red-500", badge: "bg-red-500/10 text-red-400 border-red-500/20" },
                        "Guest Security Risk": { border: "border-orange-300", bg: "bg-orange-500/5", icon: "bg-orange-500/15 text-orange-500", badge: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
                        "Reputation Risk": { border: "border-amber-300", bg: "bg-amber-500/5", icon: "bg-amber-500/15 text-amber-500", badge: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
                        "Property Damage Risk": { border: "border-orange-300", bg: "bg-orange-500/5", icon: "bg-orange-500/15 text-orange-500", badge: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
                        "Revenue Risk": { border: "border-purple-300", bg: "bg-purple-500/5", icon: "bg-purple-500/15 text-purple-500", badge: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
                        "Guest Comfort Risk": { border: "border-blue-300", bg: "bg-blue-500/5", icon: "bg-blue-500/15 text-blue-500", badge: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
                        "Guest Experience Risk": { border: "border-cyan-300", bg: "bg-cyan-500/5", icon: "bg-cyan-500/15 text-cyan-500", badge: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" },
                        "Operational Risk": { border: "border-zinc-300", bg: "bg-zinc-500/5", icon: "bg-zinc-500/15 text-zinc-400", badge: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" }
                      };

                      const categoryIcons = {
                        "Pool": Droplet, "Spa & Sauna": Sparkles, "Fitness Center": Dumbbell, "Kids Facility": Baby,
                        "In-Room Appliances": Zap, "Noise Complaint": Volume2, "Safety Concern": ShieldAlert,
                        "Parking": ParkingCircle, "Lost & Found": Search, "AC Issues": Snowflake,
                        "Water Leakage": Droplets, "Hot Water": Flame, "Door Access": DoorClosed,
                        "Lighting": Lightbulb, "Electrical": Zap, "Plumbing": Wrench,
                        "Furniture": Armchair, "Bathroom Fixtures": ShowerHead, "Window & Curtain": Blinds,
                        "Elevator": ArrowUpDown, "Room Condition": Home, "Ventilation": Wind,
                        "TV Issues": Tv, "WiFi Issues": Wifi, "Phone System": Phone,
                        "Room Cleanliness": Sparkles, "Bathroom Cleanliness": Droplet, "Public Area": Building2,
                        "Linen Quality": Shirt, "Amenities": Package, "Pest Control": Bug,
                        "Room Odor": Wind, "Room Readiness": CheckCircle, "Check-in Delay": Clock,
                        "Check-out Delay": Clock, "Reservation Error": FileWarning, "Room Assignment": Building2,
                        "Billing Error": Receipt, "Service Response": Bell, "Food Quality": UtensilsCrossed,
                        "Food Safety": ShieldAlert, "Breakfast Service": Coffee, "Restaurant Service": UtensilsCrossed,
                        "Room Service": Bell, "Beverage": Coffee, "Menu Availability": UtensilsCrossed
                      };

                      const colors = impactColors[problem.business_impact] || impactColors["Operational Risk"];
                      const IconComponent = categoryIcons[problem.dashboard_label] || AlertCircle;
                      const isTopPriority = idx === 0;

                      return (
                        <div
                          key={problem.root_problem_id}
                          className={`p-4 border rounded-md ${colors.border} ${isTopPriority ? colors.bg : ''} hover:bg-surface-highlight/50 transition-colors`}
                          data-testid={`recurring-${problem.root_problem_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-orange-400 w-4 shrink-0">{idx + 1}</span>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${colors.icon}`}>
                              <IconComponent size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground">
                                {problem.dashboard_label}
                              </p>
                              <p className="text-[10px] text-muted-foreground mt-1.5">
                                {problem.sample_rooms?.slice(0, 3).join(", ")}
                                {problem.sample_rooms?.length > 3 && ` +${problem.sample_rooms.length - 3} more`}
                              </p>
                            </div>
                            <div className="text-right shrink-0 hidden md:block">
                              <p className="text-[10px] text-muted-foreground">Trend</p>
                              <div className={`flex items-center gap-0.5 text-sm ${
                                problem.trend_status === 'UP' ? 'text-red-400' :
                                problem.trend_status === 'DOWN' ? 'text-green-400' : 'text-muted-foreground'
                              }`}>
                                {problem.trend_status === 'UP' ? <TrendingUp size={12} /> :
                                 problem.trend_status === 'DOWN' ? <TrendingDown size={12} /> : null}
                                {problem.trend_pct > 0 ? '+' : ''}{problem.trend_pct}%
                              </div>
                              <p className="text-[9px] text-muted-foreground">vs {problem.prev_count} prev</p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xl font-bold">{problem.current_count}</p>
                              <p className="text-[10px] text-muted-foreground">total</p>
                              {problem.active_count != null && (
                                <p className="text-[10px] text-orange-400 mt-0.5">{problem.active_count} aktif</p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 mt-3 pl-[52px]">
                            <button
                              onClick={() => {
                                const { start, end } = getActiveDateRange();
                                const dateParams = start ? `&start_date=${start.toISOString()}&end_date=${end.toISOString()}` : "";
                                sessionStorage.setItem("dashboard_scroll_pos", document.getElementById("main-scroll-container")?.scrollTop || 0);
                                navigate(`/issues?root_problem_id=${problem.root_problem_id}&exclude_closed=true${dateParams}`);
                              }}
                              className="text-[10px] px-2.5 py-1 rounded-sm border border-border hover:bg-muted/50 transition-colors"
                            >
                              View Active ({problem.active_count})
                            </button>
                            <button
                              onClick={() => {
                                const { start, end } = getActiveDateRange();
                                const dateParams = start ? `&start_date=${start.toISOString()}&end_date=${end.toISOString()}` : "";
                                sessionStorage.setItem("dashboard_scroll_pos", document.getElementById("main-scroll-container")?.scrollTop || 0);
                                navigate(`/issues?root_problem_id=${problem.root_problem_id}${dateParams}`);
                              }}
                              className="text-[10px] px-2.5 py-1 rounded-sm border border-border hover:bg-muted/50 transition-colors"
                            >
                              View All ({problem.current_count})
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* 5.6 Titik Panas - Rooms/Areas with Most Engineering Defects */}
          <Card className="orbit-card" data-testid="top-rooms-hotspot">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                Rooms & Areas with Most Reports
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Open engineering defects (damage/breakage) • {
                  period === "this_month" ? "This Month" :
                  period === "prev_month" ? "Previous Month" :
                  period === "ytd" ? "Year to Date" :
                  period === "custom" ? "Custom Range" : "Last 30 Days"
                }
              </p>
            </CardHeader>
            <CardContent>
              {topRooms.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Flame className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No engineering defects in this period</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {topRooms.map((r, idx) => {
                    const maxCount = topRooms[0]?.count || 1;
                    const pct = Math.max(8, Math.round((r.count / maxCount) * 100));
                    return (
                      <button
                        key={r.room_number}
                        onClick={() => { setSelectedTopRoom(r); setTopRoomDialogOpen(true); }}
                        className="w-full text-left group"
                        data-testid={`top-room-${r.room_number}`}
                      >
                        <div className="flex items-center gap-3 py-1.5">
                          <span className="text-xs font-bold text-muted-foreground w-5 shrink-0 text-right">{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-foreground group-hover:text-orange-400 transition-colors">
                                {r.room_number}
                              </span>
                              <span className="text-sm font-bold text-orange-400">{r.count}x</span>
                            </div>
                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-orange-500/70 group-hover:bg-orange-500 rounded-full transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Command Queue preview - disembunyikan, sudah ada halaman terpisah */}
          {/* 5.4 Command Queue - disembunyikan, sudah ada halaman terpisah */}
          {false && (
          <Card className="orbit-card lg:col-span-2" data-testid="command-queue">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-sm uppercase tracking-wider">
                Command Queue
              </CardTitle>
              <Link to="/issues">
                <Button variant="ghost" size="sm" className="text-primary">
                  View All <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentIssues.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No issues found</p>
                ) : (
                  recentIssues.map((issue) => (
                    <Link
                      key={issue.id}
                      to={`/issues/${issue.id}`}
                      className="block p-3 bg-surface-highlight/50 border border-border rounded-sm hover:border-primary/50 transition-colors"
                      data-testid={`issue-${issue.issue_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs text-primary">{issue.issue_number}</span>
                          <Badge className={getSeverityColor(issue.final_severity)}>{issue.final_severity}</Badge>
                          {issue.guest_type === "VIP" && (
                            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                              <Crown size={10} className="mr-1" /> VIP
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {issue.notified_at && (
                            <Smartphone size={14} className="text-blue-400" title="Notification sent" />
                          )}
                          {issue.first_response_at && (
                            <Eye size={14} className="text-emerald-400" title="Acknowledged" />
                          )}
                          <Badge className={getStatusColor(issue.status)}>{issue.status}</Badge>
                        </div>
                      </div>
                      <p className="text-sm mt-2 text-muted-foreground line-clamp-1">
                        Room {issue.room_number} • {issue.guest_name}
                      </p>
                    </Link>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
          )}
        </div>

        {/* Department Drill-Down Dialog */}
        <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wider flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                {getDeptFullName(selectedDept)} Issues
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-3 pr-4">
                {deptIssues.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No issues found</p>
                ) : (
                  deptIssues.map((issue) => (
                    <Link
                      key={issue.id}
                      to={`/issues/${issue.id}`}
                      onClick={() => setDeptDialogOpen(false)}
                      className="block p-4 bg-surface-highlight/50 border border-border rounded-sm hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-primary">{issue.issue_number}</span>
                          <Badge className={getSeverityColor(issue.final_severity)}>{issue.final_severity}</Badge>
                          <Badge className={getStatusColor(issue.status)}>{issue.status}</Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(issue.system_logged_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm mb-1">Room {issue.room_number} • {issue.guest_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {issue.description}
                      </p>
                    </Link>
                  ))
                )}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Category Drill-Down Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wider">
                {selectedCategory}
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-3 pr-4">
                {categoryIssues.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No issues found</p>
                ) : (
                  categoryIssues.map((issue) => (
                    <Link
                      key={issue.id}
                      to={`/issues/${issue.id}`}
                      className="block p-3 rounded-lg bg-surface hover:bg-surface-highlight border border-border/50 transition-colors"
                      onClick={() => setCategoryDialogOpen(false)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={getSeverityColor(issue.final_severity)}>
                            {issue.final_severity}
                          </Badge>
                          <span className="text-sm font-medium">{issue.issue_number}</span>
                        </div>
                        <Badge className={
                          issue.sentiment_label === 'POSITIVE' ? 'bg-emerald-500/10 text-emerald-500' :
                          issue.sentiment_label === 'NEGATIVE' ? 'bg-red-500/10 text-red-500' :
                          'bg-zinc-500/10 text-zinc-400'
                        }>
                          {issue.sentiment_score?.toFixed(1)} - {issue.sentiment_label}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Room {issue.room_number} • {issue.guest_name}
                      </p>
                      <p className="text-sm mt-1 line-clamp-2">
                        {issue.description}
                      </p>
                    </Link>
                  ))
                )}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Prediction Detail Dialog */}
        <Dialog open={predictionDialogOpen} onOpenChange={setPredictionDialogOpen}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">
                Recurring Defects - Room {selectedPrediction?.room}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {selectedPrediction?.category} ({selectedPrediction?.frequency}x)
              </p>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-2">
                {selectedPrediction?.issues?.map((issue, idx) => (
                  <Link
                    key={issue.id || idx}
                    to={`/issues/${issue.id}`}
                    className="block p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors"
                    onClick={() => setPredictionDialogOpen(false)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-amber-500">{issue.issue_number}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {issue.incident_occurred_at ? new Date(issue.incident_occurred_at).toLocaleDateString('id-ID') : '-'}
                      </span>
                    </div>
                    <p className="text-sm text-foreground">{issue.description}</p>
                    <div className="flex gap-1 mt-1">
                      {issue.defect_departments?.map((d) => (
                        <Badge key={d} variant="outline" className="text-[10px] text-red-400 border-red-400/30">
                          {d}
                        </Badge>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        <Dialog open={topRoomDialogOpen} onOpenChange={setTopRoomDialogOpen}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">
                {selectedTopRoom?.room_number}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {selectedTopRoom?.count} report{selectedTopRoom?.count === 1 ? "" : "s"} in this period
              </p>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-2">
                {selectedTopRoom?.issues?.map((issue, idx) => {
                  const defectBadge = getDefectTypeBadge(issue.defect_type);
                  return (
                  <Link
                    key={issue.id || idx}
                    to={`/issues/${issue.id}`}
                    className="block p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors"
                    onClick={() => setTopRoomDialogOpen(false)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-amber-500">{issue.issue_number}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {issue.system_logged_at ? new Date(issue.system_logged_at).toLocaleDateString('id-ID') : '-'}
                      </span>
                    </div>
                    <p className="text-sm text-foreground">{issue.description}</p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {defectBadge && (
                        <Badge className={`text-[10px] border ${defectBadge.className}`}>{defectBadge.label}</Badge>
                      )}
                      {issue.category && (
                        <Badge variant="outline" className="text-[10px]">{issue.category}</Badge>
                      )}
                      {issue.final_severity && (
                        <Badge className={`text-[10px] border ${getSeverityColor(issue.final_severity)}`}>{issue.final_severity}</Badge>
                      )}
                      {issue.status && (
                        <Badge className={`text-[10px] border ${getStatusColor(issue.status)}`}>{issue.status}</Badge>
                      )}
                    </div>
                  </Link>
                  );
                })}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Top Issue Detail Dialog */}
        <Dialog open={topIssueDialogOpen} onOpenChange={setTopIssueDialogOpen}>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">
                {selectedTopIssue?.category_name}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
              {selectedTopIssue?.count} issues
{selectedTopIssue?.trend_pct != null && (
  <> | Trend: {selectedTopIssue?.trend_direction === 'up' ? '↑' : selectedTopIssue?.trend_direction === 'down' ? '↓' : '→'} {Math.abs(selectedTopIssue.trend_pct)}%</>
)}
              </p>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-2">
                {selectedTopIssue?.issues?.map((issue, idx) => (
                  <Link
                    key={issue.id || idx}
                    to={`/issues/${issue.id}`}
                    className="block p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors"
                    onClick={() => setTopIssueDialogOpen(false)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-orange-500">{issue.issue_number}</span>
                      <span className="text-[10px] text-muted-foreground">
                        Room {issue.room_number} | {issue.incident_occurred_at ? new Date(issue.incident_occurred_at).toLocaleDateString('id-ID') : '-'}
                      </span>
                    </div>
                    <p className="text-sm text-foreground">{issue.description}...</p>
                  </Link>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* SLA Breaches Dialog */}
        <Dialog open={slaDialogOpen} onOpenChange={setSlaDialogOpen}>
          <DialogContent className="bg-card border-border max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">SLA Breaches ({slaBreaches.length})</DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-2">
                {slaBreaches.map((item) => (
                  <Link
                    key={item.id}
                    to={`/issues/${item.id}`}
                    className="block p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-colors"
                    onClick={() => setSlaDialogOpen(false)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-amber-500">{item.issue_number}</span>
                      <span className="text-xs text-muted-foreground">Room {item.room_number}</span>
                    </div>
                    <p className="text-sm mb-2">{item.description}...</p>
                    <div className="flex gap-4 text-xs">
                      <span>Target: <strong>{item.target_time}</strong></span>
                      <span>Actual: <strong className="text-red-400">{item.actual_duration}</strong></span>
                      <span>Delay: <strong className="text-amber-500">{item.delay_duration}</strong></span>
                      <Badge variant="outline" className="text-[10px]">{item.category || 'N/A'}</Badge>
                    </div>
                  </Link>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Pending Items Dialog */}
        <Dialog open={pendingDialogOpen} onOpenChange={setPendingDialogOpen}>
          <DialogContent className="bg-card border-border max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg flex items-center gap-2">
                <Pause className="w-5 h-5 text-purple-400" />
                Pending Watchlist ({pendingItems.length})
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh]">
              {pendingItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Pause className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No pending items</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingItems.map((item) => (
                    <Link
                      key={item.id}
                      to={`/issues/${item.id}`}
                      className="block p-3 bg-purple-500/5 border border-purple-500/20 rounded-sm hover:bg-purple-500/10 transition-colors"
                      onClick={() => setPendingDialogOpen(false)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-purple-400">{item.issue_number}</span>
                          <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-400">
                            {item.pending_reason_display}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Room {item.room_number}</span>
                          <Badge className="bg-purple-500/20 text-purple-300 text-[10px]">
                            {item.age_days} days
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-foreground mb-1">{item.description}</p>
                      {item.pending_note && (
                        <p className="text-xs text-muted-foreground italic">Note: {item.pending_note}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-[10px]">{item.department}</Badge>
                        <span>•</span>
                        <span>{item.guest_name}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Dashboard;
